// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CreateUsefulLifeRevisionParameters } from '@/asset/api/create-useful-life-revision';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormNumberTextField } from '@/components/form-number-text-field';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  USEFUL_LIFE_REVISION_REASON_OPTIONS,
  usefulLifeRevisionReasonSchema,
} from '@/constants/useful-life-revision';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const usefulLifeMonthsSchema = z
  .number({
    invalid_type_error: 'Useful life is required',
    required_error: 'Useful life is required',
  })
  .nullable()
  .refine((value): value is number => value !== null, {
    message: 'Useful life is required',
  })
  .pipe(
    z
      .number()
      .int('Useful life must be a whole number')
      .min(1, 'Useful life must be at least 1 month')
      .max(480, 'Useful life cannot exceed 480 months')
  );

const formSchema = z
  .object({
    effectiveDate: dateSchema
      .nullable()
      .refine((date): date is DateTime => date !== null, {
        message: 'Effective date is required',
      })
      .refine((date) => date >= MINIMUM_DATE, {
        message: 'Effective date cannot be before January 1, 1970',
      })
      .refine((date) => date <= DateTime.now().endOf('day'), {
        message: 'Effective date cannot be in the future',
      }),
    usefulLifeMonths: usefulLifeMonthsSchema,
    reason: usefulLifeRevisionReasonSchema.nullable(),
    notes: z.string().nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.reason === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Reason is required',
        path: ['reason'],
      });
    }

    if (
      value.reason === 'OTHER' &&
      (!value.notes || value.notes.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Notes are required when reason is Other',
        path: ['notes'],
      });
    }
  });

type FormInputData = z.input<typeof formSchema>;
type FormData = z.output<typeof formSchema>;

function getDefaultValues(): FormInputData {
  return {
    effectiveDate: DateTime.now().startOf('day'),
    usefulLifeMonths: 60,
    reason: null,
    notes: '',
  };
}

interface Props {
  isOpen: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (parameters: CreateUsefulLifeRevisionParameters) => void;
}

export function UsefulLifeRevisionDialog(props: Props) {
  const form = useForm<FormInputData, unknown, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  });

  const reason = form.watch('reason');
  const notesLabel = reason === 'OTHER' ? 'Notes' : 'Notes (optional)';

  useEffect(() => {
    form.reset(getDefaultValues());
  }, [form, props.isOpen]);

  const handleSubmit: SubmitHandler<FormData> = (data) => {
    props.onSubmit({
      effectiveDate: data.effectiveDate,
      usefulLifeMonths: data.usefulLifeMonths,
      reason: data.reason as NonNullable<FormData['reason']>,
      notes: data.notes?.trim() || null,
    });
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={props.onClose}>
      <DialogTitle>Record useful life revision</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info">
          <AlertTitle variant="subtitle2">Mid-life only</AlertTitle>
          <Typography variant="body2">
            Use revisions to record useful life <strong>changes</strong> that
            occur mid-life. If you need to restate an asset's{' '}
            <strong>entire</strong> useful life retroactively, update the
            asset's original useful life.
          </Typography>
        </Alert>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
            <FormDatePicker
              control={form.control}
              disableFuture
              label="Effective date"
              name="effectiveDate"
            />
            <FormNumberTextField
              allowNegative={false}
              control={form.control}
              fullWidth
              label="Useful life (months)"
              maximumFractionDigits={0}
              name="usefulLifeMonths"
              numeric
            />
          </Stack>
          <Controller
            control={form.control}
            name="reason"
            render={({ field }) => (
              <Autocomplete
                disableClearable={false}
                fullWidth
                options={USEFUL_LIFE_REVISION_REASON_OPTIONS}
                value={
                  USEFUL_LIFE_REVISION_REASON_OPTIONS.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                onChange={(_, value) => field.onChange(value?.value ?? null)}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(form.formState.errors.reason)}
                    helperText={form.formState.errors.reason?.message}
                    label="Reason"
                    size="small"
                  />
                )}
              />
            )}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            error={Boolean(form.formState.errors.notes)}
            helperText={form.formState.errors.notes?.message}
            label={notesLabel}
            {...form.register('notes')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={props.isPending}
          onClick={form.handleSubmit(handleSubmit)}
        >
          Record revision
        </Button>
      </DialogActions>
    </Dialog>
  );
}
